import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import {
  getClasses,
  getClassStudents,
  getClassProgress,
  getTeacherStudents,
  createClass,
  rotateClassCode,
  kickStudentFromClass
} from '../../services/api';
import './Sidebar.css';

const Sidebar = ({ role, onClassSelect, selectedClassId, onClassCreated, onRequestClose }) => {
  const navigate = useNavigate();
  const { handleRequest } = useApi();
  const [classes, setClasses] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [classProgress, setClassProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [creating, setCreating] = useState(false);
  const [rotatingClassId, setRotatingClassId] = useState(null);
  const [removingStudentId, setRemovingStudentId] = useState(null);

  useEffect(() => {
    if (role === 'teacher' || role === 'admin' || role === 'superadmin') {
      const fetchData = async () => {
        try {
          const classesResponse = await handleRequest(getClasses);
          setClasses(classesResponse.data || []);
        } catch (error) {
          console.error('Failed to fetch classes:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [role, handleRequest]);

  const handleCreateClass = async (e) => {
    e.preventDefault();
    if (!newClassName.trim()) return;

    setCreating(true);
    try {
      const response = await handleRequest(createClass, { name: newClassName.trim() });
      setClasses(prev => [response.data, ...prev]);
      setNewClassName('');
      setShowCreateForm(false);
      if (onClassCreated) {
        onClassCreated(response.data);
      }
    } catch (error) {
      console.error('Failed to create class:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleRotateCode = async (classId, e) => {
    e.stopPropagation(); // Prevent class selection
    if (!window.confirm('Rotere klassekoden? Den gamle koden vil slutte å fungere.')) return;

    setRotatingClassId(classId);
    try {
      const response = await handleRequest(rotateClassCode, classId);
      setClasses(prev => prev.map(c => 
        (c._id === classId || c.id === classId) ? response.data : c
      ));
    } catch (error) {
      console.error('Failed to rotate code:', error);
    } finally {
      setRotatingClassId(null);
    }
  };

  useEffect(() => {
    if ((role === 'teacher' || role === 'admin' || role === 'superadmin') && selectedClassId) {
      const fetchClassData = async () => {
        try {
          const [studentsResponse, progressResponse] = await Promise.all([
            handleRequest(getClassStudents, selectedClassId).catch(() => ({ data: [] })),
            handleRequest(getClassProgress, selectedClassId).catch(() => ({ data: null }))
          ]);
          setClassStudents(studentsResponse.data || []);
          setClassProgress(progressResponse.data);
        } catch (error) {
          console.error('Failed to fetch class data:', error);
        }
      };
      fetchClassData();
    } else {
      setClassStudents([]);
      setClassProgress(null);
    }
  }, [selectedClassId, role, handleRequest]);

  const handleRemoveStudent = async (studentId, studentName, e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    if (!window.confirm(`Fjern ${studentName} fra denne klassen?`)) return;

    setRemovingStudentId(studentId);
    try {
      await handleRequest(kickStudentFromClass, selectedClassId, studentId);
      // Remove student from local state
      setClassStudents(prev => prev.filter(s => (s._id || s.id) !== studentId));
      // Refresh progress data
      const progressResponse = await handleRequest(getClassProgress, selectedClassId).catch(() => ({ data: null }));
      setClassProgress(progressResponse.data);
    } catch (error) {
      console.error('Failed to remove student:', error);
    } finally {
      setRemovingStudentId(null);
    }
  };

  if (role !== 'teacher' && role !== 'admin' && role !== 'superadmin') {
    return null;
  }

  return (
    <div className="sidebar">
      <div className="sidebar-section">
        <div className="sidebar-header">
          <h3 className="sidebar-title">Klasser</h3>
          <button
            className="create-class-btn"
            onClick={() => setShowCreateForm(!showCreateForm)}
            title="Opprett ny klasse"
          >
            +
          </button>
        </div>
        
        {showCreateForm && (
          <form onSubmit={handleCreateClass} className="create-class-form">
            <input
              type="text"
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              placeholder="Klassenavn"
              className="class-name-input"
              autoFocus
              disabled={creating}
            />
            <div className="create-class-actions">
              <button type="submit" className="submit-create-btn" disabled={creating || !newClassName.trim()}>
                {creating ? 'Oppretter...' : 'Opprett'}
              </button>
              <button
                type="button"
                className="cancel-create-btn"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewClassName('');
                }}
                disabled={creating}
              >
                Avbryt
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="sidebar-loading">Laster...</p>
        ) : classes.length === 0 ? (
          <p className="sidebar-empty">Ingen klasser ennå</p>
        ) : (
          <div className="classes-list">
            {classes.map((classroom) => (
              <div
                key={classroom._id || classroom.id}
                className={`class-item ${selectedClassId === (classroom._id || classroom.id) ? 'active' : ''}`}
                onClick={() => {
                  onClassSelect(classroom._id || classroom.id);
                }}
              >
                <div className="class-item-main">
                  <div className="class-name">{classroom.name}</div>
                  <div className="class-code">
                    <span className="class-code-label">Kode:</span>
                    {classroom.joinCode}
                  </div>
                </div>
                <button
                  className="rotate-code-btn"
                  onClick={(e) => handleRotateCode(classroom._id || classroom.id, e)}
                  disabled={rotatingClassId === (classroom._id || classroom.id)}
                  title="Rotere bli-med-kode – generer en ny kode. Den gamle koden vil slutte å fungere."
                >
                  {rotatingClassId === (classroom._id || classroom.id) ? (
                    <span className="rotating-spinner">⟳</span>
                  ) : (
                    '⟳'
                  )}
                  <span className="rotate-tooltip">Rotere bli-med-kode – generer en ny kode. Den gamle koden vil slutte å fungere.</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedClassId && (
        <>
          <div className="sidebar-section">
            <h3 className="sidebar-title">Klasseliste</h3>
            {classStudents.length === 0 ? (
              <p className="sidebar-empty">Ingen elever i denne klassen ennå</p>
            ) : (
              <div className="students-list">
                {classStudents.map((student) => (
                  <div key={student._id || student.id} className="student-item">
                    <div 
                      className="student-info clickable"
                      onClick={() => navigate(`/students/${student._id || student.id}/progress`)}
                      title="Klikk for å se elevens fremdrift"
                    >
                      <div className="student-name">{student.name}</div>
                      <div className="student-email">{student.email}</div>
                    </div>
                    <button
                      className="remove-student-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveStudent(student._id || student.id, student.name, e);
                      }}
                      disabled={removingStudentId === (student._id || student.id)}
                      title="Fjern elev fra klassen"
                    >
                      {removingStudentId === (student._id || student.id) ? (
                        <span className="removing-spinner">⋯</span>
                      ) : (
                        '×'
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sidebar-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 className="sidebar-title">Elevfremdrift</h3>
              <button
                className="view-progress-btn"
                onClick={() => {
                  navigate(`/classes/${selectedClassId}/progress`);
                  if (onRequestClose) onRequestClose();
                }}
                title="Vis detaljert klassefremdrift"
              >
                Vis detaljer
              </button>
            </div>
            {!classProgress ? (
              <p className="sidebar-empty">Ingen fremdrift registrert ennå for denne klassen</p>
            ) : (
              <div className="progress-summary">
                <div className="progress-stat">
                  <span className="progress-label">Antall elever:</span>
                  <span className="progress-value">{classProgress.totalStudents || 0}</span>
                </div>
                <div className="progress-stat">
                  <span className="progress-label">Fullførte leksjoner:</span>
                  <span className="progress-value">{classProgress.completedLessons || 0}</span>
                </div>
                <div className="progress-stat">
                  <span className="progress-label">Snittresultat:</span>
                  <span className="progress-value">{classProgress.averageScore ? `${classProgress.averageScore}%` : 'N/A'}</span>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Sidebar;
